package com.bikompanion.data

import de.jensklingenberg.ktorfit.Ktorfit
import dev.zacsweers.metro.BindingContainer
import dev.zacsweers.metro.ContributesTo
import dev.zacsweers.metro.Provides
import io.ktor.client.HttpClient
import io.ktor.client.plugins.DefaultRequest
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.header
import io.ktor.serialization.kotlinx.json.json
import com.bikompanion.data.cache.BikeDataCache
import com.bikompanion.data.gbfs.GbfsBikeNetworkRepository
import com.bikompanion.domain.BikeNetworkRepository
import com.bikompanion.domain.di.AppScope

@ContributesTo(AppScope::class)
@BindingContainer
object NetworkProviders {
    @Provides
    fun provideHttpClient(): HttpClient =
        HttpClient {
            // So a non-2xx response throws a typed ClientRequestException/ServerResponseException
            // (see CityBikesRepository's rate-limit handling) instead of being handed to the JSON
            // decoder as if it were a normal body.
            expectSuccess = true
            install(ContentNegotiation) {
                json(apiJson)
            }
            // Station data is polled on a fixed cadence, so a request that never answers has to
            // give up rather than hold its network's turn indefinitely.
            install(HttpTimeout) {
                requestTimeoutMillis = 15_000
                connectTimeoutMillis = 10_000
            }
            // The key is for local development only; release builds use the public tier without
            // one. Platforms that can't send it themselves reach the API through a dev proxy that
            // attaches it instead — see [cityBikesEndpoint].
            if (BuildKonfig.IS_DEBUG && cityBikesEndpoint.sendsApiKeyHeader) {
                install(DefaultRequest) {
                    header("x-api-key", BuildKonfig.CITY_BIKES_DEBUG_API_KEY)
                }
            }
            installLogging(tag = "BikeData")
        }

    @Provides
    fun provideCityBikesKtorfit(httpClient: HttpClient): Ktorfit =
        Ktorfit
            .Builder()
            .baseUrl(cityBikesEndpoint.baseUrl)
            .httpClient(httpClient)
            .build()

    @Provides
    fun provideCityBikesApi(ktorfit: Ktorfit): CityBikesApi = ktorfit.createCityBikesApi()

    /**
     * The single place that decides where bike data comes from.
     *
     * Both repositories are constructed either way — each is a thin wrapper over an API
     * object and neither touches the network until it is called — which keeps this to a
     * plain `when` over [BikeDataSource.Active] instead of DI plumbing that would have to be
     * unpicked to switch back.
     */
    @Provides
    fun provideBikeNetworkRepository(
        cityBikes: CityBikesRepository,
        gbfs: GbfsBikeNetworkRepository,
        cache: BikeDataCache,
    ): BikeNetworkRepository =
        BikeNetworkRepositoryImpl(
            delegate = when (BikeDataSource.Active) {
                BikeDataSource.CityBikes -> cityBikes
                BikeDataSource.Gbfs -> gbfs
            },
            cache = cache,
        )
}
